import React, { useCallback } from 'react'
import { FormikProps } from 'formik'
import {
  DatePickerV2,
  TextInputV4,
  labraUIDateFormat,
  TextAreaV4,
  BoxV4,
} from '@labrav/react-components'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { debounce, get, isEmpty } from 'lodash'

import { useStyles } from './ResellerMicrosoftOfferDetail.styles'
import { OfferRecipientV3 } from '../../../common/components/OfferRecipientV3/OfferRecipientV3'
import { OfferRecipientWithTable } from '../../../common/components/OfferRecipientWithTable/OfferRecipientWithTable'
import clsx from 'clsx'
import { DateTime } from 'luxon'
import {
  MicrosoftFormFAQs,
  MicrosoftFormFAQsItem,
} from './MicrosoftFormFAQs/MicrosoftFormFAQs'
import { useSelector } from '../../../store'
import {
  FieldMetadata,
  FieldMetadataField,
  OfferRecipients,
} from '../../../modules/privateOffer/types'
import { Product } from '../../../modules/productsListing/types'

export type ResellerMicrosoftOfferDetailProps = {
  formikProps: FormikProps<Record<string, unknown>>
  product: Product
}

export const ResellerMicrosoftOfferDetail: React.FC<
  ResellerMicrosoftOfferDetailProps
> = ({ formikProps, product }) => {
  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = formikProps

  const fieldMetadata = useSelector(
    state =>
      state.resaleAuthorizationsInCreation
        ?.currentResaleAuthorizationInCreation?.[product?.productId as string]
        ?.fieldMetadata
  ) as FieldMetadata | undefined

  const isOfferRecipientsFromTemplate =
    (
      (fieldMetadata?.offerRecipientsList as Record<string, unknown>)
        ?.offerRecipients as OfferRecipients[]
    )?.length > 0 || false

  const isOfferRecipientsReadOnly = (
    fieldMetadata?.offerRecipients as FieldMetadataField
  )?.readOnly
  const isOfferRecipientsHidden = (
    fieldMetadata?.offerRecipients as FieldMetadataField
  )?.hidden

  const isExpirationDateHidden = (
    fieldMetadata?.offerExpirationDate as FieldMetadataField
  )?.hidden

  const isExpirationDateReadOnly = (
    fieldMetadata?.offerExpirationDate as FieldMetadataField
  )?.readOnly

  const isBuyerNameHidden = (fieldMetadata?.buyerName as FieldMetadataField)
    ?.hidden

  const isBuyerNameReadOnly = (fieldMetadata?.buyerName as FieldMetadataField)
    ?.readOnly

  const isBuyerBillingAccountIdHidden = (
    fieldMetadata?.buyerBillingAccountId as FieldMetadataField
  )?.hidden

  const isBuyerBillingAccountIdReadOnly = (
    fieldMetadata?.buyerBillingAccountId as FieldMetadataField
  )?.readOnly

  const isOfferNameHidden = (fieldMetadata?.mppoName as FieldMetadataField)
    ?.hidden

  const isOfferNameReadOnly = (fieldMetadata?.mppoName as FieldMetadataField)
    ?.readOnly

  const isOfferDescriptionHidden = (
    fieldMetadata?.mppoDescription as FieldMetadataField
  )?.hidden

  const isOfferDescriptionReadOnly = (
    fieldMetadata?.mppoDescription as FieldMetadataField
  )?.readOnly

  const isEmailCustomMessageHidden = (
    fieldMetadata?.emailCustomMessage as FieldMetadataField
  )?.hidden

  const isEmailCustomMessageReadOnly = (
    fieldMetadata?.emailCustomMessage as FieldMetadataField
  )?.readOnly

  const debouncedSetFieldValue = useCallback(
    debounce((name, value) => {
      setFieldValue(name, value)
    }, 500),
    []
  )

  const offerExpirationDateField = {
    title: 'Authorization expiration date',
    humanReadableKey: 'Authorization expiration date',
    placeHolder: 'Authorization expiration date',
    id: 'acceptBy',
    isRequired: false,
    isReadOnly: false,
    isCustom: false,
    dataType: 'date',
    minDate: DateTime.now().plus({ days: 1 }).toISODate(),
  }

  const formChangeHandler = useCallback(
    (keyName: string) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        debouncedSetFieldValue(keyName, event.target.value)
      },
    [debouncedSetFieldValue]
  )
  const { classes } = useStyles()

  const handleDateChange = useCallback(async (date: string | null) => {
    const updatedDate = date ? DateTime.fromISO(date).toISO() : ''
    await setFieldValue('acceptBy', updatedDate)
    await setFieldTouched('acceptBy', true)
  }, [])
  return (
    <div data-testid="details-section">
      <BoxV4
        heading={'Reseller authorization details'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '16px', height: '16px' }}
          />
        }
        secondaryActions={
          <MicrosoftFormFAQs
            item={MicrosoftFormFAQsItem.AUTHORIZATION_DETAIL}
          />
        }
      >
        <div className={classes.sectionContainer}>
          <div className={classes.companyDetailsContainer}>
            {!isBuyerNameHidden && (
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="companyName"
                  title="Buyer name"
                  type="text"
                  placeHolder=""
                  value={values.companyName as string}
                  maxNumber={72}
                  isError={
                    !!(
                      get(touched, 'companyName', false) &&
                      get(errors, 'companyName', false)
                    )
                  }
                  isReadOnly={isBuyerNameReadOnly}
                  onChange={formChangeHandler('companyName')}
                  isRequired={false}
                  errorText={
                    (!!get(touched, 'companyName', false) &&
                      get(errors, 'companyName', '')) ||
                    ''
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>
            )}
            {!isBuyerBillingAccountIdHidden && (
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="beneficiaryId"
                  title="Buyer billing account ID"
                  type="text"
                  placeHolder=""
                  value={(values.beneficiaryId || '') as string}
                  isError={
                    !!(
                      get(touched, 'beneficiaryId', false) &&
                      get(errors, 'beneficiaryId', false)
                    )
                  }
                  onChange={formChangeHandler('beneficiaryId')}
                  isReadOnly={isBuyerBillingAccountIdReadOnly}
                  isRequired={false}
                  errorText={
                    (!!get(touched, 'beneficiaryId', false) &&
                      get(errors, 'beneficiaryId', '')) ||
                    ''
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>
            )}
          </div>
        </div>

        <div className={classes.companyDetailsContainer}>
          {!isOfferNameHidden && (
            <div className={classes.inputWidth}>
              <TextInputV4
                keyName="mppoName"
                title="Offer name"
                type="text"
                placeHolder=""
                value={values.mppoName as string}
                maxNumber={72}
                isError={
                  !!(
                    get(touched, 'mppoName', false) &&
                    get(errors, 'mppoName', false)
                  )
                }
                onChange={formChangeHandler('mppoName')}
                isReadOnly={isOfferNameReadOnly}
                isRequired={false}
                errorText={
                  (!!get(touched, 'mppoName', false) &&
                    get(errors, 'mppoName', '')) ||
                  ''
                }
                showSpinner={false}
                onBlur={handleBlur}
              />
            </div>
          )}
          {!isExpirationDateHidden && (
            <div
              className={clsx(
                classes.inputWidth,
                classes.datePicker,
                classes.datePickerErrorOutline
              )}
            >
              <DatePickerV2
                views={['year', 'month', 'day']}
                size="small"
                format={labraUIDateFormat}
                field={{
                  ...offerExpirationDateField,
                  defaultValue: values?.acceptBy,
                  isReadOnly: isExpirationDateReadOnly,
                }}
                key={'acceptBy'}
                handleDateChange={handleDateChange}
                showErrorHelper={
                  !!touched.acceptBy && !isEmpty(errors.acceptBy)
                }
                fieldId={'acceptBy'}
                errorHelperText={(!!touched.acceptBy && errors.acceptBy) || ''}
              />
            </div>
          )}
        </div>
        {!isOfferDescriptionHidden && (
          <div className={classes.sectionContainer}>
            <TextAreaV4
              keyName="mppoDescription"
              title="Offer description (Optional)"
              rows={10}
              maxNumber={1000}
              height={'80px'}
              type="string"
              placeHolder="Offer description (Optional)"
              value={(values.mppoDescription || '') as string}
              isError={!!(errors.mppoDescription && touched.mppoDescription)}
              onChange={formChangeHandler('mppoDescription')}
              isReadOnly={isOfferDescriptionReadOnly}
              isRequired={false}
              errorText={
                (!!touched.mppoDescription && errors.mppoDescription) || ''
              }
              showSpinner={false}
              onBlur={handleBlur}
            />
          </div>
        )}

        {isOfferRecipientsFromTemplate ? (
          <>
            <OfferRecipientWithTable
              offerRecipients={
                (fieldMetadata?.offerRecipientsList as Record<string, unknown>)
                  ?.offerRecipients as OfferRecipients[]
              }
              formik={
                formikProps as unknown as FormikProps<Record<string, unknown>>
              }
            />
          </>
        ) : (
          <>
            {!isOfferRecipientsHidden && (
              <div
                className={classes.sectionContainer}
                data-testid="recipients-details"
              >
                <div className={classes.textContainer}>
                  <Typography className={classes.sectionHeading}>
                    Reseller recipients
                  </Typography>
                </div>
                <OfferRecipientV3
                  formik={
                    formikProps as unknown as FormikProps<
                      Record<string, unknown>
                    >
                  }
                  isReadOnly={isOfferRecipientsReadOnly}
                />
              </div>
            )}
          </>
        )}

        {!isEmailCustomMessageHidden && (
          <div className={classes.sectionContainer}>
            <TextAreaV4
              keyName="emailCustomMessage"
              title="Add custom email message to be sent to recipients"
              rows={10}
              maxNumber={1000}
              height="80px"
              type="string"
              placeHolder="Add custom email message to be sent to recipients"
              value={(values.emailCustomMessage || '') as string}
              isError={
                !!(errors.emailCustomMessage && touched.emailCustomMessage)
              }
              onChange={formChangeHandler('emailCustomMessage')}
              isReadOnly={isEmailCustomMessageReadOnly}
              isRequired={false}
              errorText={
                (!!touched.emailCustomMessage && errors.emailCustomMessage) ||
                ''
              }
              showSpinner={false}
              onBlur={handleBlur}
            />
          </div>
        )}
      </BoxV4>
    </div>
  )
}
